import React from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import { AnimatePresence, motion } from "framer-motion";
import { GameConstant, GameRules } from "@uno/protocol";
import { activeHouseRules } from "utils/HouseRules";
import { getCardContent } from "utils/CardContent";
import { CARD_WIDTH, CARD_HEIGHT } from "types/Card";
import "css/cards-v2.scss";

type Props = {
    isOpen: boolean;
    onClose: () => void;
    houseRules?: GameRules;
};

const CARD_SCALE = 0.3;
const CARD_W = Math.round(CARD_WIDTH * CARD_SCALE);
const CARD_H = Math.round(CARD_HEIGHT * CARD_SCALE);

const MiniCard = ({ type }: { type: string }) => (
    <div style={{ width: CARD_W, height: CARD_H, position: "relative", flexShrink: 0 }}>
        <div className="card-container" style={{ transform: `scale(${CARD_SCALE})`, transformOrigin: "top left", position: "absolute", filter: "drop-shadow(-1px 1px 3px rgba(0,0,0,0.5))" }}>
            <div className="card-inner">
                <div className={`card ${type.toLowerCase()}`}><div>{getCardContent(type).corner}</div><div>{getCardContent(type).corner}</div><div>{getCardContent(type).center}</div></div>
            </div>
        </div>
    </div>
);

const SectionTitle = ({ children }: { children: React.ReactNode }) => (
    <h3 className="text-white font-changa-one text-base mt-5 mb-2 first:mt-0">{children}</h3>
);

const RulesDrawer = ({ isOpen, onClose, houseRules }: Props) => {
    const { t } = useTranslation();
    const activeRules = houseRules ? activeHouseRules(houseRules) : [];

    return ReactDOM.createPortal(
        <AnimatePresence>
            {isOpen && (
                <>
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/60 z-40"
                        onClick={onClose}
                    />
                    <motion.div
                        initial={{ x: "-100%" }}
                        animate={{ x: 0 }}
                        exit={{ x: "-100%" }}
                        transition={{ duration: 0.25, ease: "easeOut" }}
                        className="fixed top-0 left-0 bottom-0 w-80 md:w-96 bg-[#1a1a2e] border-r border-white/10 z-50 flex flex-col"
                    >
                        <div className="flex items-center justify-between px-4 pb-4 border-b border-white/10" style={{ paddingTop: "calc(var(--safe-top) + 1.25rem)" }}>
                            <h2 className="text-white font-changa-one text-lg">{t("rules.title")}</h2>
                            <button onClick={onClose} className="text-white/40 hover:text-white/70 transition-colors cursor-pointer">
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        <div className="flex-1 overflow-y-auto px-5 py-5 text-white/60 text-xs leading-normal text-justify panel-scrollbar">

                            {/* House rules of this game, before the standard rules they change */}
                            {activeRules.length > 0 && (
                                <div className="mb-5 rounded-xl bg-white/5 border border-white/10 px-3 py-3 text-left">
                                    <SectionTitle>{t("rules.house.title")}</SectionTitle>
                                    <ul className="space-y-2.5">
                                        {activeRules.map((rule) => (
                                            <li key={rule.key} className="flex items-start gap-3">
                                                <img src={rule.icon} alt="" width={32} height={32} className="w-8 h-8 shrink-0" />
                                                <p><span className="text-white font-bold">{t(rule.name)}</span> — {t(rule.description)}</p>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            {/* Goal */}
                            <SectionTitle>{t("rules.goal.title")}</SectionTitle>
                            <p>{t("rules.goal.description", { startingCards: GameConstant.startingCardsCount })}</p>
                            <div className="flex justify-center gap-1 py-2">
                                <MiniCard type="r3" />
                                <MiniCard type="b7" />
                                <MiniCard type="g5" />
                                <MiniCard type="y2" />
                                <MiniCard type="rblock" />
                                <MiniCard type="b10" />
                                <MiniCard type="gplus" />
                                <MiniCard type="ncolor" />
                            </div>

                            {/* How to play */}
                            <SectionTitle>{t("rules.turns.title")}</SectionTitle>
                            <p>{t("rules.turns.description")}</p>
                            <div className="flex justify-center items-center gap-1.5 py-2">
                                <MiniCard type="r5" />
                                <span className="text-white/60 text-xs font-bold shrink-0">&rarr;</span>
                                <MiniCard type="r9" />
                                <MiniCard type="b5" />
                                <MiniCard type="ncolor" />
                            </div>
                            <p className="text-white/30 text-[10px] text-center">{t("rules.turns.match_hint")}</p>

                            {/* Special cards */}
                            <SectionTitle>{t("rules.special_cards.title")}</SectionTitle>
                            <div className="space-y-3 py-1">
                                <div className="flex items-center gap-3">
                                    <MiniCard type="rblock" />
                                    <p>{t("rules.special_cards.skip")}</p>
                                </div>
                                <div className="flex items-center gap-3">
                                    <MiniCard type="breverse" />
                                    <p>{t("rules.special_cards.reverse")}</p>
                                </div>
                                <div className="flex items-center gap-3">
                                    <MiniCard type="gplus" />
                                    <p>{t("rules.special_cards.draw_two")}</p>
                                </div>
                                <div className="flex items-center gap-3">
                                    <MiniCard type="ncolor" />
                                    <p>{t("rules.special_cards.wild")}</p>
                                </div>
                                <div className="flex items-center gap-3">
                                    <MiniCard type="ndraw" />
                                    <p>{t("rules.special_cards.wild_draw_four")}</p>
                                </div>
                            </div>

                            {/* Stacking — hidden when this game turned it off */}
                            {!houseRules?.noStacking && (
                                <>
                                    <SectionTitle>{t("rules.stacking.title")}</SectionTitle>
                                    <p>{t("rules.stacking.description")}</p>
                                    <div className="flex justify-center items-center gap-2 py-2">
                                        <MiniCard type="rplus" />
                                        <span className="text-white/60 text-xs font-bold shrink-0">+</span>
                                        <MiniCard type="bplus" />
                                        <span className="text-white/60 text-xs font-bold shrink-0">+</span>
                                        <MiniCard type="ndraw" />
                                        <span className="text-white/40 text-[10px] font-bold">= 8</span>
                                    </div>
                                </>
                            )}

                            {/* Timer */}
                            <SectionTitle>{t("rules.timer.title")}</SectionTitle>
                            <p>{t("rules.timer.description", { roundDuration: GameConstant.maxRoundDuration, maxMissed: GameConstant.maxMissedRounds })}</p>

                            {/* Scoring */}
                            <SectionTitle>{t("rules.scoring.title")}</SectionTitle>
                            <p>{t("rules.scoring.description")}</p>
                            <div className="space-y-2 py-2">
                                <div className="flex items-center gap-3 bg-white/5 rounded-lg px-3 py-2">
                                    <div className="flex gap-0.5">
                                        <MiniCard type="b3" />
                                        <MiniCard type="y10" />
                                    </div>
                                    <span className="text-white/50 text-[10px] flex-1">{t("rules.scoring.number_cards")}</span>
                                </div>
                                <div className="flex items-center gap-3 bg-white/5 rounded-lg px-3 py-2">
                                    <div className="flex gap-0.5">
                                        <MiniCard type="rblock" />
                                        <MiniCard type="gplus" />
                                    </div>
                                    <span className="text-white/50 text-[10px] flex-1">{t("rules.scoring.action_cards")}</span>
                                </div>
                                <div className="flex items-center gap-3 bg-white/5 rounded-lg px-3 py-2">
                                    <div className="flex gap-0.5">
                                        <MiniCard type="ncolor" />
                                        <MiniCard type="ndraw" />
                                    </div>
                                    <span className="text-white/50 text-[10px] flex-1">{t("rules.scoring.wild_cards")}</span>
                                </div>
                            </div>

                            {/* Tiebreaker */}
                            <SectionTitle>{t("rules.tiebreaker.title")}</SectionTitle>
                            <p>{t("rules.tiebreaker.description")}</p>

                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>,
        document.body
    );
};

export default RulesDrawer;
